import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";
import {
  ActionButton,
  DevSection,
  EntityTable,
  JsonViewer,
  ResultPanel,
  StatusBadge,
} from "./-_dev-components";
import { createFileRoute } from "@tanstack/react-router";
import { toDocumentId } from "@/lib/route-params";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ResultState = {
  data: unknown;
  error: unknown;
  timestamp: number;
  label: string;
};

// ---------------------------------------------------------------------------
// Documents Playground Page
// ---------------------------------------------------------------------------

function DocumentsPlaygroundPage() {
  const workspaces = useQuery(api.workspaces.getMyWorkspaces, {});
  const [selectedWorkspaceIdx, setSelectedWorkspaceIdx] = useState(0);
  const workspace = workspaces?.[selectedWorkspaceIdx];
  const workspaceId = workspace?._id ?? null;

  const documents = useQuery(
    api.document_queries.listDocuments,
    workspaceId ? { workspaceId } : "skip",
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [result, setResult] = useState<ResultState | null>(null);

  const selectedDoc = documents?.find((d) => d.id === selectedId) ?? null;

  function handleSelect(id: string) {
    setSelectedId(id);
    setResult(null);
  }

  function makeResultHandler(label: string) {
    return (r: { data: unknown; error: unknown; timestamp: number }) =>
      setResult({ ...r, label });
  }

  return (
    <div className="flex h-full overflow-hidden font-mono text-sm">
      {/* Left panel — document list */}
      <div className="flex w-80 shrink-0 flex-col border-r border-border p-3">
        {/* Workspace selector */}
        {workspaces && workspaces.length > 1 && (
          <select
            value={selectedWorkspaceIdx}
            onChange={(e) => setSelectedWorkspaceIdx(Number(e.target.value))}
            className="mb-2 rounded border border-border bg-background px-2 py-1 text-xs"
          >
            {workspaces.map((ws, i) => (
              <option key={ws._id} value={i}>
                {ws.name ?? ws.slug}
              </option>
            ))}
          </select>
        )}
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Documents ({documents?.length ?? "…"})
        </h2>
        <EntityTable
          rows={documents ?? []}
          isLoading={documents === undefined}
          selectedId={selectedId}
          onSelect={handleSelect}
          getId={(d) => d.id}
          columns={[
            {
              header: "ID",
              render: (d) => (
                <span className="font-mono text-xs text-muted-foreground">
                  …{d.id.slice(-8)}
                </span>
              ),
            },
            {
              header: "File name",
              render: (d) => (
                <span title={d.fileName}>
                  {d.fileName.length > 22
                    ? `${d.fileName.slice(0, 22)}…`
                    : d.fileName}
                </span>
              ),
            },
            {
              header: "Status",
              render: (d) => (
                <StatusBadge status={d.summaryStatus ?? undefined} />
              ),
            },
          ]}
        />
      </div>

      {!selectedDoc ? (
        <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">
          Select a document to inspect and run actions
        </div>
      ) : (
        <>
          {/* Middle panel — actions */}
          <div className="flex w-[520px] shrink-0 flex-col border-r border-border overflow-hidden">
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Header */}
              <div className="space-y-0.5">
                <h2 className="font-semibold truncate">
                  {selectedDoc.fileName}
                </h2>
                <span className="text-xs text-muted-foreground font-mono">
                  {selectedDoc.id}
                </span>
              </div>

              {/* Raw data viewer */}
              <JsonViewer data={selectedDoc} label="Raw Document" />

              {/* Action sections */}
              <AISummarySection
                selectedId={selectedId!}
                onResult={makeResultHandler}
              />
              <ChecklistSection
                selectedId={selectedId!}
                onResult={makeResultHandler}
              />
              <MetadataSection
                selectedId={selectedId!}
                initialFileName={selectedDoc.fileName}
                initialSummary={selectedDoc.summary ?? ""}
                initialSummaryStatus={selectedDoc.summaryStatus ?? "completed"}
                onResult={makeResultHandler}
              />
              <DangerSection
                selectedId={selectedId!}
                onResult={makeResultHandler}
              />
            </div>

            {/* Sticky result panel — always visible, never scrolls away */}
            <div className="shrink-0 border-t border-border bg-background p-3">
              {result ? (
                <ResultPanel result={result} />
              ) : (
                <p className="text-xs text-muted-foreground">
                  Run an action to see the result here.
                </p>
              )}
            </div>
          </div>

          {/* Right panel — live events feed */}
          <div className="flex flex-1 flex-col overflow-hidden">
            <LiveEventsPanel documentId={selectedId!} />
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live Events Panel
// ---------------------------------------------------------------------------

function LiveEventsPanel({ documentId }: { documentId: string }) {
  const docId = toDocumentId(documentId);
  const status = useQuery(api.document_queries.getSummaryStatus, {
    documentId: docId,
  });

  const activities = useQuery(api.document_queries.getDocumentActivities, {
    documentId: docId,
    limit: 30,
  });

  const isProcessing = status?.isGenerating ?? false;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Live Events
        </span>
        {isProcessing && (
          <span className="flex items-center gap-1.5 text-xs text-yellow-600">
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-yellow-500" />
            Processing…
          </span>
        )}
        {!isProcessing && activities !== undefined && (
          <span className="text-xs text-muted-foreground">
            {activities.length} events
          </span>
        )}
      </div>

      {/* Status strip */}
      <div className="flex items-center gap-3 border-b border-border bg-muted/20 px-4 py-2">
        <span className="text-xs text-muted-foreground">Summary:</span>
        <StatusBadge status={status?.summaryStatus} />
        {isProcessing && (
          <span className="text-xs text-yellow-600 animate-pulse">
            generating…
          </span>
        )}
        {status?.canRetry && !isProcessing && (
          <span className="text-xs text-muted-foreground">(retriable)</span>
        )}
      </div>

      {/* Event list */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {activities === undefined && (
          <div className="flex h-20 items-center justify-center text-xs text-muted-foreground">
            Loading…
          </div>
        )}
        {activities?.length === 0 && (
          <div className="flex h-20 items-center justify-center text-xs text-muted-foreground">
            No events yet
          </div>
        )}
        {activities?.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event row
// ---------------------------------------------------------------------------

type ActivityType =
  | "upload"
  | "update"
  | "download"
  | "delete"
  | "tag"
  | "summary"
  | "failure"
  | "version";

interface Activity {
  id: string;
  type: ActivityType;
  description: string;
  timestamp: number;
  userName: string | null;
  metadata: unknown;
}

const TYPE_STYLES: Record<ActivityType, { pill: string; bar: string }> = {
  failure: {
    pill: "bg-red-100 text-red-700 border-red-300",
    bar: "bg-red-400",
  },
  summary: {
    pill: "bg-green-100 text-green-700 border-green-300",
    bar: "bg-green-400",
  },
  upload: {
    pill: "bg-blue-100 text-blue-700 border-blue-300",
    bar: "bg-blue-400",
  },
  version: {
    pill: "bg-purple-100 text-purple-700 border-purple-300",
    bar: "bg-purple-400",
  },
  tag: {
    pill: "bg-orange-100 text-orange-700 border-orange-300",
    bar: "bg-orange-400",
  },
  update: {
    pill: "bg-zinc-100 text-zinc-600 border-zinc-300",
    bar: "bg-zinc-400",
  },
  delete: {
    pill: "bg-red-100 text-red-700 border-red-300",
    bar: "bg-red-400",
  },
  download: {
    pill: "bg-zinc-100 text-zinc-600 border-zinc-300",
    bar: "bg-zinc-400",
  },
};

function EventRow({ event }: { event: Activity }) {
  const [expanded, setExpanded] = useState(false);
  const style = TYPE_STYLES[event.type] ?? TYPE_STYLES.update;
  const ago = formatAgo(event.timestamp);

  return (
    <div className="rounded border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-start gap-2 px-2 py-1.5 text-left hover:bg-muted/30 transition-colors"
      >
        {/* Colour bar */}
        <span className={`mt-0.5 h-3 w-1 shrink-0 rounded-full ${style.bar}`} />

        {/* Type pill */}
        <span
          className={`shrink-0 rounded border px-1 py-px text-[10px] font-semibold uppercase ${style.pill}`}
        >
          {event.type}
        </span>

        {/* Description */}
        <span className="flex-1 text-xs leading-tight">
          {event.description}
        </span>

        {/* Time */}
        <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
          {ago}
        </span>

        {/* Expand indicator */}
        {event.metadata != null && (
          <span className="shrink-0 text-[10px] text-muted-foreground">
            {expanded ? "▲" : "▼"}
          </span>
        )}
      </button>

      {expanded && event.metadata != null && (
        <div className="border-t border-border bg-muted/10 px-2 py-1.5">
          <pre className="text-[10px] leading-relaxed overflow-auto max-h-40">
            {JSON.stringify(event.metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function formatAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ---------------------------------------------------------------------------
// AI Summary section
// ---------------------------------------------------------------------------

function AISummarySection({
  selectedId,
  onResult,
}: {
  selectedId: string;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const [customInstructions, setCustomInstructions] = useState("");
  const docId = toDocumentId(selectedId);

  const regenerateSummary = useMutation(
    api.document_mutations.regenerateSummary,
  );
  const generateDocumentTags2 = useMutation(
    api.document_mutations.generateDocumentTags2,
  );

  return (
    <DevSection title="AI Summary">
      <div className="space-y-2">
        <label className="block text-xs text-muted-foreground">
          Custom instructions (optional)
        </label>
        <textarea
          value={customInstructions}
          onChange={(e) => setCustomInstructions(e.target.value)}
          rows={2}
          placeholder="Leave empty for default prompt…"
          className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <ActionButton
          label="Regenerate Summary"
          onResult={onResult("Regenerate Summary")}
          onClick={async () =>
            regenerateSummary({
              documentId: docId,
              customInstructions: customInstructions || undefined,
            })
          }
        />
        <ActionButton
          label="Generate Tags"
          onResult={onResult("Generate Tags")}
          onClick={async () =>
            generateDocumentTags2({
              documentId: docId,
            })
          }
        />
      </div>
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// Checklist Integration section
// ---------------------------------------------------------------------------

function ChecklistSection({
  selectedId,
  onResult,
}: {
  selectedId: string;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const docId = toDocumentId(selectedId);
  const findMatchingChecklistItems = useMutation(
    api.document_mutations.findMatchingChecklistItems,
  );

  return (
    <DevSection title="Checklist Integration">
      <ActionButton
        label="Find Matching Checklist Items"
        onResult={onResult("Find Matching Checklist Items")}
        onClick={async () =>
          findMatchingChecklistItems({
            documentId: docId,
          })
        }
      />
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// Document Metadata section
// ---------------------------------------------------------------------------

function MetadataSection({
  selectedId,
  initialFileName,
  initialSummary,
  initialSummaryStatus,
  onResult,
}: {
  selectedId: string;
  initialFileName: string;
  initialSummary: string;
  initialSummaryStatus: string;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const [fileName, setFileName] = useState(initialFileName);
  const [summary, setSummary] = useState(initialSummary);
  const [summaryStatus, setSummaryStatus] = useState(initialSummaryStatus);

  const renameDocument = useMutation(api.document_mutations.renameDocument);
  const update = useMutation(api.document_mutations.update);

  return (
    <DevSection title="Document Metadata">
      <div className="space-y-1">
        <label className="block text-xs text-muted-foreground">File name</label>
        <div className="flex gap-2">
          <input
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            className="flex-1 rounded border border-border bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <ActionButton
            label="Rename"
            onResult={onResult("Rename")}
            onClick={async () =>
              renameDocument({
                id: selectedId as Id<"documents">,
                fileName,
              })
            }
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="block text-xs text-muted-foreground">Summary</label>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={3}
          className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>

      <div className="space-y-1">
        <label className="block text-xs text-muted-foreground">
          Summary status (free-text — test validation)
        </label>
        <div className="flex gap-2">
          <input
            value={summaryStatus}
            onChange={(e) => setSummaryStatus(e.target.value)}
            className="flex-1 rounded border border-border bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <ActionButton
            label="Update"
            onResult={onResult("Update (summary/status)")}
            onClick={async () =>
              update({
                id: selectedId as Id<"documents">,
                summary,
                summaryStatus: summaryStatus as string,
              })
            }
          />
        </div>
      </div>
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// Danger Zone section
// ---------------------------------------------------------------------------

function DangerSection({
  selectedId,
  onResult,
}: {
  selectedId: string;
  onResult: (
    label: string,
  ) => (r: { data: unknown; error: unknown; timestamp: number }) => void;
}) {
  const [confirmId, setConfirmId] = useState("");
  const deleteDocument = useMutation(api.document_mutations.deleteDocument);

  const shortId = selectedId.slice(-8);
  const confirmed = confirmId === shortId;

  return (
    <DevSection title="Danger Zone" danger>
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Type the last 8 chars of the document ID to enable delete:
          <span className="ml-1 font-mono">{shortId}</span>
        </p>
        <div className="flex gap-2">
          <input
            value={confirmId}
            onChange={(e) => setConfirmId(e.target.value)}
            placeholder={shortId}
            className="flex-1 rounded border border-border bg-background px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-red-500"
          />
          <ActionButton
            label="Delete"
            variant="destructive"
            disabled={!confirmed}
            onResult={(r) => {
              onResult("Delete Document")(r);
              setConfirmId("");
            }}
            onClick={async () =>
              deleteDocument({ id: selectedId as Id<"documents"> })
            }
          />
        </div>
      </div>
    </DevSection>
  );
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/dev/documents")({
  component: DocumentsPlaygroundPage,
});
